import React, { useMemo } from 'react';
import { useFormState } from 'react-final-form';
import styled from 'styled-components';
import { ObjectSchema } from 'yup';

import PrimaryButton from '../../../../_common/button';
import { FormValidation } from '../../../../_common/context/BookingContext';
import { Heading1, Heading2 } from '../../../../_common/Typography';
import { useScopedTranslation } from '../../../../_common/utils/hooks/useScopedTranslation';
import {
  FieldsListQuery_category_fields as Field,
  Section,
} from '../../../../types/api';
import { GroupT } from '../types';

import FieldDefinition from './FieldDefinition';

const FloatyProgressButton = styled(PrimaryButton)`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  padding: 0.5rem;
  height: auto;
`;

const ProgressBar = styled.div<{ percentage: number }>`
  position: relative;
  border: 2px solid white;
  height: 0.8rem;
  width: 100%;
  margin-top: 0.125rem;

  &::after {
    position: absolute;
    content: '';
    left: 1px;
    top: 1px;
    bottom: 1px;
    width: calc((100% - 2px) * ${({ percentage }) => percentage});
    transition: width 200ms cubic-bezier(1, 0, 0, 1);
    background-color: white;
  }
`;

const section = 'w-full space-y-10 lg:w-1/2 lg:px-12';

interface Props {
  fieldDefinitions: Array<GroupT | Field>;
  submitting?: boolean;
  validationSchema: ObjectSchema<object>;
  validationType?: FormValidation;
}

export default function SectionedForm(props: Props) {
  const { fieldDefinitions, submitting, validationSchema, validationType } =
    props;
  const { t } = useScopedTranslation();
  const { errors, values } = useFormState();

  const questionFieldDefinitions = useMemo(
    () =>
      fieldDefinitions.filter((field) => field.section === Section.QUESTION),
    [fieldDefinitions],
  );

  const requiredFields = useMemo(
    () => fieldDefinitions.filter((field) => field.requiredForQualification),
    [fieldDefinitions],
  );

  const validRequiredFields = useMemo(
    () =>
      requiredFields.filter((field) => values[field.id] && !errors[field.id]),
    [requiredFields, values, errors],
  );

  return (
    <div className="flex flex-col mx-auto pb-20 max-w-8xl space-y-6">
      <div className="lg:px-12">
        <div className="hidden">
          <Heading1>{t('customer_form.title')}</Heading1>
        </div>
      </div>
      <div className="flex flex-wrap justify-center w-full space-y-4 lg:flex-no-wrap lg:space-y-0">
        <div className={section}>
          <div className="lg:h-8">
            <Heading2>{t('customer_form.personal_details')}</Heading2>
          </div>
          {fieldDefinitions
            .filter((field) => field.section === Section.FIELD)
            .map((field) => (
              <FieldDefinition
                fieldDefinition={field}
                key={`${field.id}_${validationType}`}
                validationSchema={validationSchema}
              />
            ))}
        </div>
        {questionFieldDefinitions.length > 0 && (
          <div className={`${section} lg:border-l lg:border-gray-200`}>
            <div className="-mb-4 mt-8 lg:mb-0 lg:mt-0 lg:h-8">
              <Heading2>{t('customer_form.questions')}</Heading2>
            </div>
            {questionFieldDefinitions.map((fd) => (
              <FieldDefinition
                fieldDefinition={fd}
                key={fd.id}
                validationSchema={validationSchema}
              />
            ))}
          </div>
        )}
      </div>
      <FloatyProgressButton
        disabled={Object.keys(errors).length !== 0}
        loading={submitting}
        type="submit"
      >
        <div className="flex flex-col items-center justify-center w-full">
          <p className="text-white">
            {requiredFields.length - validRequiredFields.length === 0
              ? t('button.save')
              : `${validRequiredFields.length}/${requiredFields.length}`}
          </p>
          <ProgressBar
            percentage={validRequiredFields.length / requiredFields.length || 1}
          />
        </div>
      </FloatyProgressButton>
    </div>
  );
}
